import type {
  ActivityListResponse,
  CommentDto,
  ItemRefDto,
  ItemRelationsDto,
  ItemRevisionDto,
  RelationType,
} from '@project-os/contracts';
import type {
  ActivityFilterInput,
  CreateCommentInput,
  CreateRelationInput,
  UpdateCommentInput,
} from '@project-os/validation';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './client';
import { itemKeys } from './items';
import { notificationKeys } from './notifications';

// --- Связи ---------------------------------------------------------------

export const relationKeys = {
  list: (projectId: string, itemId: string) =>
    ['projects', projectId, 'items', itemId, 'relations'] as const,
  targets: (projectId: string, itemId: string, relationType: string, search: string) =>
    ['projects', projectId, 'items', itemId, 'relation-targets', relationType, search] as const,
};

export function useItemRelations(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: relationKeys.list(projectId, itemId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ItemRelationsDto>(`/projects/${projectId}/items/${itemId}/relations`, { signal }),
    enabled: Boolean(itemId),
  });
}

/** Элементы, доступные для связывания. Запрос идёт только при открытой форме. */
export function useRelationTargets(
  projectId: string,
  itemId: string | null,
  relationType: RelationType,
  search: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: relationKeys.targets(projectId, itemId ?? '', relationType, search),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ relationType });
      if (search) params.set('search', search);
      return apiRequest<ItemRefDto[]>(
        `/projects/${projectId}/items/${itemId}/relation-targets?${params.toString()}`,
        { signal },
      );
    },
    enabled: enabled && Boolean(itemId),
  });
}

function useRelationInvalidation(projectId: string, itemId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'items'] });
    void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'activity'] });
    void queryClient.invalidateQueries({ queryKey: itemKeys.detail(projectId, itemId) });
  };
}

export function useCreateRelation(projectId: string, itemId: string) {
  const invalidate = useRelationInvalidation(projectId, itemId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateRelationInput) =>
      apiRequest<ItemRelationsDto>(`/projects/${projectId}/items/${itemId}/relations`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (relations) => {
      queryClient.setQueryData(relationKeys.list(projectId, itemId), relations);
      invalidate();
    },
  });
}

export function useDeleteRelation(projectId: string, itemId: string) {
  const invalidate = useRelationInvalidation(projectId, itemId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (relationId: string) =>
      apiRequest<{ fromItemId: string }>(`/projects/${projectId}/relations/${relationId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: relationKeys.list(projectId, itemId) });
      invalidate();
    },
  });
}

// --- Комментарии ---------------------------------------------------------

export const commentKeys = {
  list: (projectId: string, itemId: string) =>
    ['projects', projectId, 'items', itemId, 'comments'] as const,
};

export function useComments(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: commentKeys.list(projectId, itemId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CommentDto[]>(`/projects/${projectId}/items/${itemId}/comments`, { signal }),
    enabled: Boolean(itemId),
  });
}

/** Все изменения ленты возвращают её целиком — кэш обновляется ответом. */
function useCommentMutation<TInput>(
  projectId: string,
  itemId: string,
  request: (input: TInput) => Promise<CommentDto[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (comments) => {
      queryClient.setQueryData(commentKeys.list(projectId, itemId), comments);
      void queryClient.invalidateQueries({ queryKey: itemKeys.detail(projectId, itemId) });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'activity'] });
      // Новый комментарий — событие ленты уведомлений: без этого счётчик
      // у колокольчика оставался прежним до перезагрузки.
      void queryClient.invalidateQueries({ queryKey: notificationKeys.feed(projectId) });
    },
  });
}

export function useCreateComment(projectId: string, itemId: string) {
  return useCommentMutation(projectId, itemId, (input: CreateCommentInput) =>
    apiRequest<CommentDto[]>(`/projects/${projectId}/items/${itemId}/comments`, {
      method: 'POST',
      body: input,
    }),
  );
}

export function useUpdateComment(projectId: string, itemId: string) {
  return useCommentMutation(
    projectId,
    itemId,
    (input: UpdateCommentInput & { commentId: string }) =>
      apiRequest<CommentDto[]>(`/projects/${projectId}/comments/${input.commentId}`, {
        method: 'PATCH',
        body: { bodyMd: input.bodyMd },
      }),
  );
}

export function useDeleteComment(projectId: string, itemId: string) {
  return useCommentMutation(projectId, itemId, (commentId: string) =>
    apiRequest<CommentDto[]>(`/projects/${projectId}/comments/${commentId}`, {
      method: 'DELETE',
    }),
  );
}

// --- История версий ------------------------------------------------------

export function useItemRevisions(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: ['projects', projectId, 'items', itemId ?? '', 'revisions'],
    queryFn: ({ signal }) =>
      apiRequest<ItemRevisionDto[]>(`/projects/${projectId}/items/${itemId}/revisions`, {
        signal,
      }),
    enabled: Boolean(itemId),
  });
}

// --- Журнал действий -----------------------------------------------------

export type ActivityQuery = Pick<
  ActivityFilterInput,
  'userId' | 'action' | 'entityType' | 'from' | 'to'
>;

export function useActivity(projectId: string, query: ActivityQuery) {
  return useInfiniteQuery({
    queryKey: ['projects', projectId, 'activity', query],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams();
      if (query.userId) params.set('userId', query.userId);
      if (query.action?.length) params.set('action', query.action.join(','));
      if (query.entityType) params.set('entityType', query.entityType);
      if (query.from) params.set('from', query.from);
      if (query.to) params.set('to', query.to);
      if (pageParam) params.set('cursor', pageParam);

      const search = params.toString();
      return apiRequest<ActivityListResponse>(
        `/projects/${projectId}/activity${search ? `?${search}` : ''}`,
        { signal },
      );
    },
    getNextPageParam: (lastPage) => lastPage.page.nextCursor ?? undefined,
    enabled: Boolean(projectId),
  });
}
